import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleSharpIcon from "@mui/icons-material/CheckCircleSharp";
import SnackBar from "components/SnackBar/SnackBar";
import InfoModal from "components/CenterPoint/InfoModal/InfoModal";
import classes from "./RequestForAccess.module.css";
import Button from "@mui/material/Button";
import { REQUEST_ACCESS } from "navigation/CONSTANTS";
import { Typography, styled } from "@mui/material";
import { noAuthAccessRequest, getRitmDetail } from "services/userServices";
import DOMPurify from "dompurify";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { FormLabel, RadioGroup, Radio, FormControlLabel } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

const optionTtec = [
  "AgentStateTrackerTool_WFM_Access",
  "WFM_IDM_Group_Admin",
  "AgentStateTrackerTool_TA_Access",
  "AgentStateTrackerTool_OG_Access",
  "AgentStateTrackerTool_OPS_Access",
  "AgentStateTrackerTool_GeneralAccess",
];
const optionPercepta = [
  "AAD-AgentStateTrackerTool_Cloud_WFM_Access",
  "AAD-AgentStateTrackerTool_Cloud_TA_Access",
  "AAD-AgentStateTrackerTool_Cloud_OPS_Access",
  "AAD-AgentStateTrackerTool_Cloud_General_Access",
  "AAD-AgentStateTrackerTool_Cloud_LD_Access",
];

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "400",
  border: "2px solid #000",
  shadow: "0px 0px 24px rgba(0, 0, 0, 0.5)",
  boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.5)",
  padding: "16px",
  bgcolor: "background.paper",
  maxHeight: "80vh",
  overflow: "auto",
  margin: "auto",
};

const CustomButton = styled(Button)(() => ({
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  fontSize: "12px",
  letterSpacing: "0.10em",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
  float: "right",
}));

export const isEmptyString = (value) => value?.trim() === "" || value == "";
const RequestForAccess = (props) => {
  const { handleClose, open } = props;
  const [accessType, setAccessType] = React.useState("");
  const [roleType, setRoleType] = React.useState("Ttec");
  const [comments, setComments] = React.useState("");
  const [loader, setLoader] = React.useState(false);
  const [input, setInput] = React.useState("");
  const [oracleError, setOracleError] = React.useState(false);
  const [commentError, setCommentError] = React.useState(false);
  const [isClicked, setIsClicked] = React.useState(false);
  const [activeDirectoryGroup, setActiveDirectoryGroup] = React.useState(null);
  const [aditionalDirectoryGroup, setAditionalDirectoryGroup] =
    React.useState(null);
  const [snack, setSnack] = React.useState({
    open: false,
    message: "",
    severity: "",
  });

  const [infoModal, setInfoModal] = React.useState({
    state: false,
    header: "",
    message: "",
    icon: <CheckCircleSharpIcon className={classes.icon} />,
  });

  const accessTypes = [
    "ASTT",
    "LearnPoint",
    "Onboarding",
    "Contact Roster",
    "CenterPoint",
  ];

  const resetValues = () => {
    setOracleError(false);
    setCommentError(false);
    setIsClicked(false);
    setComments("");
    setAccessType("");
    setRoleType("");
    setInput("");
    setActiveDirectoryGroup("");
  };

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleInfoClose = () => {
    setInfoModal((prev) => {
      return {
        ...prev,
        state: false,
      };
    });
  };

  const handleChange = (e) => setAccessType(e.target.value);
  const handleChangeRole = (e) => setRoleType(e.target.value);

  const handleCommentChange = (e) => {
    const rawValue = e.target.value;
    const cleanValue = DOMPurify.sanitize(rawValue, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    });
    const unsafeSQLPattern =
      /(select|insert|delete|drop|update|alter|exec|union)\b/i;
    if (unsafeSQLPattern.test(cleanValue)) {
      setCommentError(true);
      return;
    }
    setComments(cleanValue);
    setCommentError(cleanValue.length < 100);
  };

  const validateInput = (value) => {
    const oracleRegex = /^\d{7}$/;

    if (oracleRegex.test(value)) {
      setOracleError("");
      return true;
    } else {
      setOracleError("Enter a valid oracle ID");
      return false;
    }
  };

  const handleChangeText = (e) => {
    const value = e.target.value;
    setInput(value);
    validateInput(value);
  };

  const onSubmit = async () => {
    try {
      setLoader(true);
      setIsClicked(true);
      if (accessType === "") {
        setSnack({
          open: true,
          message: "Please fill Access Type!",
          severity: "error",
        });
        return;
      } else if (input === "") {
        setSnack({
          open: true,
          message: "Please fill Requestor!",
          severity: "error",
        });
        return;
      } else if (activeDirectoryGroup === null || activeDirectoryGroup === "") {
        setSnack({
          open: true,
          message: "Active Directory Group field is required!",
          severity: "error",
        });
        return;
      } else if (comments.length < 100) {
        setSnack({
          open: true,
          message: "Comments must be at least 100 characters.",
          severity: "error",
        });
        return;
      } else if (isEmptyString(comments)) {
        setSnack({
          open: true,
          message: "Empty spaces are not allowed in comments.",
          severity: "error",
        });
        return;
      } else {
        const description =
          comments +
          "\nCould you please add the following user to the Active Directory groups: " +
          `${roleType}, ${aditionalDirectoryGroup}.` +
          "\nThe user details are as follows: \nOracle ID: " +
          input;

        const response = await noAuthAccessRequest(input, description);

        if (response?.data?.status === true) {
          const objReqDetails = JSON.parse(response.data.data);
          const reqSysId = objReqDetails.result?.sys_id;
          const ritmRequest = await getRitmDetail(reqSysId);
          if (ritmRequest?.data?.status === true) {
            const number = ritmRequest.data.data?.result?.number;
            setInfoModal((prev) => {
              return {
                ...prev,
                state: true,
                message: `Access request created successfully.Here's your ticket number for your reference :${number} `,
                header: "Success",
              };
            });
            props.handleClose();
          } else {
            setSnack({
              open: true,
              message: "Something went wrong ! Please try again.",
              severity: "error",
            });
            return;
          }
        } else {
          setSnack({
            open: true,
            message: "Something went wrong ! Please try again.",
            severity: "error",
          });
          return;
        }
        resetValues();
        setIsClicked(false);
      }
    } catch (error) {
      setSnack(true, "Something went wrong !", "error");
    } finally {
      setLoader(false);
      setIsClicked(false);
    }
  };

  const handleAutocompleteChange = (value) => {
    setActiveDirectoryGroup(value);
    if (
      value === "AgentStateTrackerTool_OG_Access" ||
      value === "AgentStateTrackerTool_TA_Access" ||
      value === "AAD-AgentStateTrackerTool_Cloud_TA_Access"
    ) {
      setAditionalDirectoryGroup("ASTTAgentDetailsAccess_Med");
    } else {
      setAditionalDirectoryGroup("ASTTAgentDetailsAccess_Low");
    }
  };

  return (
    <div>
      {loader && (
        <div className={classes.loader}>
          <CircularProgress sx={{ color: "#A8A8A8" }} />
        </div>
      )}

      <InfoModal
        open={infoModal.state}
        message={infoModal.message}
        header={infoModal.header}
        icon={infoModal.icon}
        handleClose={handleInfoClose}
      />

      <Modal
        open={open}
        onClose={() => {
          resetValues();
        }}
      >
        <Box sx={{ ...style }}>
          <Typography
            sx={{
              fontFamily: "Urbanist, Verdana, Arial, sans-serif",
              fontSize: 18,
              fontWeight: "bold",
              color: "#3F5765",
              letterSpacing: "0.10em",
            }}
          >
            <SupportAgentIcon sx={{ fontSize: 26, marginRight: 1 }} />
            Request for Access
            <CloseIcon
              sx={{
                fontSize: 24,
                cursor: "pointer",
                color: "#3F5765",
                "&:hover": { color: "#d32f2f" },
                float: "right",
              }}
              onClick={() => {
                resetValues();
                handleClose();
              }}
            />
          </Typography>
          <Grid
            item
            xs={12}
            sx={{
              marginTop: "20px",
              fontSize: "15px",
              marginLeft: "10px",
            }}
          >
            <a href={REQUEST_ACCESS} rel="noreferrer" target="_blank">
              View Job Aid here
            </a>
          </Grid>
          <Grid container spacing={2} className={classes.issueContainer}>
            <Grid item xs={6}>
              <p className={classes.label}> Requestor </p>
              <TextField
                value={input}
                onChange={handleChangeText}
                error={oracleError}
                id="outlined-basic"
                label="Enter Oracle Id"
                variant="outlined"
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <p className={classes.label}>Access Type</p>
              <FormControl fullWidth className={classes.paddingRight}>
                <InputLabel>Select Access type</InputLabel>
                <Select
                  value={accessType}
                  onChange={handleChange}
                  label="Access Type"
                >
                  {accessTypes.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl>
                <FormLabel component="legend"> Choose Login Option</FormLabel>
                <RadioGroup
                  name="roleType"
                  row
                  value={roleType}
                  onChange={(e) => handleChangeRole(e)}
                >
                  <FormControlLabel
                    value={"Ttec"}
                    control={<Radio />}
                    label="@ttec.com users"
                  />
                  <FormControlLabel
                    value={"Percepta"}
                    control={<Radio />}
                    label="@percepta.com users"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <Autocomplete
                disablePortal
                freeSolo
                openOnFocus
                options={roleType === "Ttec" ? optionTtec : optionPercepta}
                value={activeDirectoryGroup}
                onChange={(e, value) => handleAutocompleteChange(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="medium"
                    placeholder="Active Directory Group"
                    label={
                      <span>
                        Active Directory Group
                        <Box component={"span"} sx={{ color: "red" }}>
                          *
                        </Box>
                      </span>
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <p className={classes.comment}>Reason for Access</p>
              <TextField
                placeholder="4000 Character Limit"
                multiline
                rows={4}
                fullWidth
                value={comments}
                onChange={handleCommentChange}
                helperText={
                  <span
                    className={
                      (comments.length > 0 && comments.length < 100) ||
                      isEmptyString(comments)
                        ? classes.helperText
                        : classes.helperTextValid
                    }
                  >
                    {`${comments ? comments.length : 0}/4000`}
                  </span>
                }
                className={classes.textField}
                FormHelperTextProps={{
                  sx: {
                    position: "absolute",
                    bottom: 8,
                    right: 8,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <span
                className={
                  (comments.length > 0 && comments.length < 100) ||
                  isEmptyString(comments)
                    ? classes.helperText
                    : classes.helperTextValid
                }
              >
                Please describe in at least 100 characters
              </span>
            </Grid>
            <Grid item xs={12}>
              <span className={classes.note}>
                Note: ISU will still require approval from ASTT owner
              </span>
            </Grid>
            <Grid item xs={12}>
              <CustomButton
                variant="contained"
                onClick={onSubmit}
                disabled={comments.length < 100 || isClicked}
              >
                Submit
              </CustomButton>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      <SnackBar snack={snack} snackReset={snackReset} />
    </div>
  );
};

export default RequestForAccess;
